import { Component } from 'react';

/**
 * Generic error boundary. Wraps each lazily-loaded tab so a render
 * failure in one tab does not take down the whole app.
 *
 * Logs the error to the console and renders a minimal recovery panel
 * with a "Try again" button that resets boundary state.
 */
export default class ErrorBoundary extends Component {
  constructor(/** @type {{ children: React.ReactNode, label?: string }} */ props) {
    super(props);
    /** @type {{ error: Error | null }} */
    this.state = { error: null };
  }

  /** @param {Error} error */
  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} info
   */
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', this.props.label ?? '', error, info);
  }

  /**
   * A "Failed to fetch dynamically imported module" / chunk-load error means
   * the lazy chunk this view needs no longer exists on the server (stale build
   * after a deploy). Re-rendering can't refetch it — only a full reload pulls
   * the fresh index.html + chunks, so hard-reload in that case.
   */
  reset = () => {
    const message = this.state.error?.message ?? '';
    const isChunkError = /dynamically imported module|Importing a module script failed|Failed to fetch|ChunkLoadError|error loading dynamically imported module/i.test(message);
    if (isChunkError) {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary__icon">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          </div>
          <h3>Something went wrong</h3>
          <p>
            {this.props.label ? <>The <strong>{this.props.label}</strong> view crashed: </> : null}
            <code>{this.state.error.message}</code>
          </p>
          <button type="button" className="btn btn-primary" onClick={this.reset}>
            <i className="fas fa-redo" aria-hidden="true" /> Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
